import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { formatCountdownMmSs } from '../../utils/session-auth-error.util';

export interface SessionExpiryDialogData {
  expiryAtMs: number;
}

export type SessionExpiryDialogResult = 'stay' | 'logout' | 'expired';

@Component({
  selector: 'app-session-expiry-dialog',
  standalone: true,
  imports: [MatDialogModule],
  template: `
    <div
      class="bh-session-expiry-dialog-inner px-6 pt-5 pb-5 bg-white dark:bg-gray-800 text-center">
      <div class="flex justify-center mb-3" aria-hidden="true">
        <div
          class="flex h-12 w-12 items-center justify-center rounded-full bg-warning/20">
          <svg
            class="h-6 w-6 text-warning"
            fill="none"
            viewBox="0 0 24 24"
            stroke-width="1.5"
            stroke="currentColor"
            aria-hidden="true">
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
        </div>
      </div>
      <h2 class="text-lg font-semibold text-[#4D4D4D] dark:text-gray-200">
        Session Expiring Soon
      </h2>
      <p
        class="mt-2 text-sm leading-relaxed text-[#4D4D4D] dark:text-gray-300 px-1">
        Your session will expire in
        <strong class="font-semibold">{{ countdown }}</strong
        >.
      </p>
      <p
        class="mt-1 text-sm leading-relaxed text-[#4D4D4D] dark:text-gray-300 px-1">
        Would you like to continue your session?
      </p>
      <div class="mt-6 flex flex-row justify-center gap-4">
        <button
          type="button"
          [mat-dialog-close]="'logout'"
          class="min-w-[96px] rounded-full border border-gray-400 bg-white px-3 py-2 text-[13px] font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-500 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700">
          Logout
        </button>
        <button
          type="button"
          [mat-dialog-close]="'stay'"
          class="min-w-[96px] rounded-full bg-primary px-3 py-2 text-[13px] font-medium text-white transition-opacity hover:opacity-90">
          Stay Logged In
        </button>
      </div>
    </div>
  `,
})
export class SessionExpiryDialogComponent implements OnInit, OnDestroy {
  readonly data = inject<SessionExpiryDialogData>(MAT_DIALOG_DATA);
  private readonly dialogRef = inject(
    MatDialogRef<SessionExpiryDialogComponent, SessionExpiryDialogResult>,
  );

  countdown = formatCountdownMmSs(this.data.expiryAtMs - Date.now());
  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private expiredEmitted = false;

  ngOnInit(): void {
    this.tickCountdown();
    this.countdownInterval = setInterval(() => this.tickCountdown(), 1000);
  }

  ngOnDestroy(): void {
    if (this.countdownInterval) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
  }

  private tickCountdown(): void {
    const remainingMs = this.data.expiryAtMs - Date.now();
    this.countdown = formatCountdownMmSs(remainingMs);
    if (remainingMs <= 0 && !this.expiredEmitted) {
      this.expiredEmitted = true;
      if (this.countdownInterval) {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
      }
      this.dialogRef.close('expired');
    }
  }
}
